import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { docsApi } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import {
  Book, Zap, Database, Brain, Network, Key, Shield, Upload,
  ChevronRight, Copy, Check, Terminal, ArrowRight, Search,
  Layers, Sparkles, GitBranch, RefreshCw, Eye, Trash2,
  Link2, Box, BarChart3, Settings, Code2, Globe,
  Loader2, AlertCircle, Pencil, Save, X, RotateCcw,
} from 'lucide-react';

/* ------------------------------------------------------------------ */
/*  Icon map — DB stores string names, we map to Lucide components     */
/* ------------------------------------------------------------------ */
const ICON_MAP = {
  Book, Zap, Database, Brain, Network, Key, Shield, Upload,
  Search, Layers, Sparkles, GitBranch, RefreshCw, Eye,
  Link2, Box, BarChart3, Settings, Code2, Globe,
  ArrowRight, Trash2,
};

/* ------------------------------------------------------------------ */
/*  Clipboard helper                                                  */
/* ------------------------------------------------------------------ */
function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button
      onClick={copy}
      className="absolute top-2 right-2 p-1.5 rounded-md bg-white/5 hover:bg-white/10 text-muted-foreground hover:text-foreground transition-colors"
      title="Copy"
    >
      {copied ? <Check className="h-3.5 w-3.5 text-green-400" /> : <Copy className="h-3.5 w-3.5" />}
    </button>
  );
}

/* ------------------------------------------------------------------ */
/*  Code block                                                        */
/* ------------------------------------------------------------------ */
function CodeBlock({ language = 'bash', title, children }) {
  const code = typeof children === 'string' ? children.trim() : '';
  return (
    <div className="relative rounded-lg border bg-[hsl(var(--card))]/80 overflow-hidden my-4">
      {title && (
        <div className="flex items-center gap-2 px-4 py-2 border-b bg-muted/30 text-xs text-muted-foreground">
          <Terminal className="h-3 w-3" />
          {title}
          <span className="ml-auto font-mono text-[10px] opacity-60">{language}</span>
        </div>
      )}
      <CopyButton text={code} />
      <pre className="p-4 overflow-x-auto text-sm leading-relaxed">
        <code className="text-foreground/90 font-mono">{code}</code>
      </pre>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Endpoint card                                                     */
/* ------------------------------------------------------------------ */
const METHOD_COLORS = {
  GET: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  POST: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
  PUT: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  DELETE: 'bg-red-500/15 text-red-400 border-red-500/30',
  PATCH: 'bg-purple-500/15 text-purple-400 border-purple-500/30',
};

function Endpoint({ method, path, description, body, response, params, query, children_content }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border rounded-lg overflow-hidden mb-3">
      <button
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-accent/30 transition-colors"
        onClick={() => setOpen(!open)}
      >
        <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded border ${METHOD_COLORS[method] || ''}`}>
          {method}
        </span>
        <code className="text-sm font-mono text-foreground/80 flex-1">{path}</code>
        <span className="text-xs text-muted-foreground hidden sm:block max-w-[280px] truncate">{description}</span>
        <ChevronRight className={`h-4 w-4 text-muted-foreground transition-transform ${open ? 'rotate-90' : ''}`} />
      </button>
      {open && (
        <div className="border-t px-4 py-4 space-y-3 bg-card/40">
          <p className="text-sm text-muted-foreground">{description}</p>
          {params && params.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-foreground mb-1">Path Parameters</p>
              <div className="space-y-1">
                {params.map(p => (
                  <div key={p.name} className="flex items-baseline gap-2 text-xs">
                    <code className="text-primary font-mono">{p.name}</code>
                    <span className="text-muted-foreground">{p.desc}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {query && query.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-foreground mb-1">Query Parameters</p>
              <div className="space-y-1">
                {query.map(q => (
                  <div key={q.name} className="flex items-baseline gap-2 text-xs">
                    <code className="text-primary font-mono">{q.name}</code>
                    <span className="text-[10px] text-muted-foreground/60">{q.required ? 'required' : 'optional'}</span>
                    <span className="text-muted-foreground">{q.desc}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {body && (
            <div>
              <p className="text-xs font-semibold text-foreground mb-1">Request Body</p>
              <CodeBlock language="json" title="Example">{body}</CodeBlock>
            </div>
          )}
          {response && (
            <div>
              <p className="text-xs font-semibold text-foreground mb-1">Response</p>
              <CodeBlock language="json" title="200 OK">{response}</CodeBlock>
            </div>
          )}
          {children_content && (
            <div className="border rounded-lg p-3 bg-muted/20 mt-2">
              <MarkdownContent>{children_content}</MarkdownContent>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Section anchored heading                                          */
/* ------------------------------------------------------------------ */
function SectionHeading({ id, icon: Icon, children }) {
  return (
    <h2 id={id} className="flex items-center gap-2 text-xl font-semibold text-foreground pt-10 pb-3 border-b scroll-mt-20">
      {Icon && <Icon className="h-5 w-5 text-primary" />}
      {children}
    </h2>
  );
}

function SubHeading({ id, children }) {
  return (
    <h3 id={id} className="text-base font-semibold text-foreground pt-6 pb-2 scroll-mt-20">
      {children}
    </h3>
  );
}

/* ------------------------------------------------------------------ */
/*  Markdown renderer with Tailwind styling                           */
/* ------------------------------------------------------------------ */
function MarkdownContent({ children }) {
  if (!children) return null;
  return (
    <ReactMarkdown
      components={{
        p: ({ children }) => <p className="text-sm text-muted-foreground leading-relaxed mb-3">{children}</p>,
        strong: ({ children }) => <strong className="text-foreground font-medium">{children}</strong>,
        em: ({ children }) => <em>{children}</em>,
        code: ({ children, inline, className }) => {
          if (inline || !className) {
            return <code className="text-primary text-xs font-mono bg-muted/30 px-1 py-0.5 rounded">{children}</code>;
          }
          // Fenced code blocks
          const lang = className?.replace('language-', '') || '';
          return <CodeBlock language={lang}>{String(children)}</CodeBlock>;
        },
        pre: ({ children }) => <>{children}</>,
        ul: ({ children }) => <ul className="list-disc list-inside space-y-1.5 ml-2 text-sm text-muted-foreground">{children}</ul>,
        ol: ({ children }) => <ol className="list-decimal list-inside space-y-1.5 ml-2 text-sm text-muted-foreground">{children}</ol>,
        li: ({ children }) => <li>{children}</li>,
        h3: ({ children }) => <h3 className="text-base font-semibold text-foreground pt-4 pb-2">{children}</h3>,
        a: ({ href, children }) => <a href={href} className="text-primary hover:underline">{children}</a>,
      }}
    >
      {children}
    </ReactMarkdown>
  );
}

/* ------------------------------------------------------------------ */
/*  Callout box                                                       */
/* ------------------------------------------------------------------ */
const CALLOUT_STYLES = {
  info: 'border-blue-500/30 bg-card/50',
  warning: 'border-amber-500/30 bg-amber-500/5',
  success: 'border-green-500/30 bg-green-500/5',
  danger: 'border-red-500/30 bg-red-500/5',
};

function Callout({ title, content, variant = 'info' }) {
  return (
    <div className={`border rounded-lg p-4 my-4 space-y-2 ${CALLOUT_STYLES[variant] || CALLOUT_STYLES.info}`}>
      {title && <p className="text-xs text-foreground font-medium">{title}</p>}
      <MarkdownContent>{content}</MarkdownContent>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Feature card                                                      */
/* ------------------------------------------------------------------ */
function FeatureCard({ icon, title, content }) {
  const Icon = ICON_MAP[icon] || Book;
  const iconColors = {
    Brain: 'text-purple-400',
    Network: 'text-blue-400',
    Sparkles: 'text-amber-400',
    GitBranch: 'text-cyan-400',
  };
  return (
    <div className="border rounded-lg p-4 space-y-2">
      <div className="flex items-center gap-2 text-foreground font-medium">
        <Icon className={`h-4 w-4 ${iconColors[icon] || 'text-primary'}`} />
        {title}
      </div>
      <p className="text-xs text-muted-foreground">{content}</p>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Steps list                                                        */
/* ------------------------------------------------------------------ */
function StepsList({ steps }) {
  if (!steps || steps.length === 0) return null;
  return (
    <div className="border rounded-lg p-4 bg-card/50 text-xs space-y-2 my-4">
      {steps.map((step, i) => (
        <div key={i} className="flex items-center gap-3">
          <span className="shrink-0 w-6 h-6 rounded-full bg-blue-500/15 text-blue-400 flex items-center justify-center text-[10px] font-bold">
            {step.number || i + 1}
          </span>
          <MarkdownContent>{step.content}</MarkdownContent>
        </div>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Admin Section Editor                                              */
/* ------------------------------------------------------------------ */
function SectionEditor({ section, onSave, onCancel }) {
  const [title, setTitle] = useState(section.title || '');
  const [icon, setIcon] = useState(section.icon || '');
  const [order, setOrder] = useState(section.order ?? 0);
  const [content, setContent] = useState(section.content || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({ title, icon, order: Number(order), content });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border rounded-lg p-4 bg-card space-y-3 my-4">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-foreground">Edit Section</p>
        <div className="flex items-center gap-2">
          <button onClick={onCancel} className="text-xs text-muted-foreground hover:text-foreground px-2 py-1 rounded border">
            <X className="h-3 w-3 inline mr-1" />Cancel
          </button>
          <button onClick={handleSave} disabled={saving} className="text-xs text-primary-foreground bg-primary hover:bg-primary/90 px-3 py-1 rounded disabled:opacity-50">
            {saving ? <Loader2 className="h-3 w-3 inline mr-1 animate-spin" /> : <Save className="h-3 w-3 inline mr-1" />}
            Save
          </button>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="text-[10px] text-muted-foreground uppercase">Title</label>
          <input value={title} onChange={e => setTitle(e.target.value)} className="w-full mt-1 px-2 py-1.5 text-sm border rounded bg-background" />
        </div>
        <div>
          <label className="text-[10px] text-muted-foreground uppercase">Icon</label>
          <input value={icon} onChange={e => setIcon(e.target.value)} className="w-full mt-1 px-2 py-1.5 text-sm border rounded bg-background" />
        </div>
        <div>
          <label className="text-[10px] text-muted-foreground uppercase">Order</label>
          <input type="number" value={order} onChange={e => setOrder(e.target.value)} className="w-full mt-1 px-2 py-1.5 text-sm border rounded bg-background" />
        </div>
      </div>
      <div>
        <label className="text-[10px] text-muted-foreground uppercase">Content (Markdown)</label>
        <textarea
          value={content}
          onChange={e => setContent(e.target.value)}
          className="w-full mt-1 px-3 py-2 text-sm border rounded bg-background font-mono min-h-[200px] resize-y"
        />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Render a single doc section from structured data                  */
/* ------------------------------------------------------------------ */
function DocSection({ section, isAdmin, onSectionUpdated }) {
  const [editing, setEditing] = useState(false);
  const sectionId = section.layerId || section.sectionId;
  const Icon = ICON_MAP[section.icon] || Book;
  const isIntro = sectionId === 'introduction';

  const handleSave = async (updates) => {
    try {
      await docsApi.update(sectionId, updates);
      setEditing(false);
      if (onSectionUpdated) onSectionUpdated();
    } catch (err) {
      console.error('Failed to save section:', err);
      alert('Failed to save: ' + (err.response?.data?.error || err.message));
    }
  };

  return (
    <section id={sectionId}>
      {/* Introduction hero header */}
      {isIntro ? (
        <div className="flex items-center gap-3 mb-2">
          <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Icon className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">{section.title}</h1>
            <p className="text-sm text-muted-foreground">Values as Infrastructure for AI</p>
          </div>
          {isAdmin && !editing && (
            <button onClick={() => setEditing(true)} className="ml-auto text-xs text-muted-foreground hover:text-foreground p-1.5 rounded border">
              <Pencil className="h-3 w-3" />
            </button>
          )}
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <SectionHeading id={sectionId} icon={Icon}>{section.title}</SectionHeading>
          {isAdmin && !editing && (
            <button onClick={() => setEditing(true)} className="mt-8 text-xs text-muted-foreground hover:text-foreground p-1.5 rounded border shrink-0">
              <Pencil className="h-3 w-3" />
            </button>
          )}
        </div>
      )}

      {/* Admin editor */}
      {editing && (
        <SectionEditor section={section} onSave={handleSave} onCancel={() => setEditing(false)} />
      )}

      {/* Section content */}
      <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
        {/* Markdown prose */}
        {section.content && <div className={isIntro ? "mt-6 space-y-4" : ""}><MarkdownContent>{section.content}</MarkdownContent></div>}

        {/* Callouts */}
        {section.callouts?.map((c, i) => <Callout key={i} {...c} />)}

        {/* Feature cards (intro) */}
        {section.featureCards?.length > 0 && (
          <div className="grid gap-4 sm:grid-cols-2 mt-6">
            {section.featureCards.map((card, i) => <FeatureCard key={i} {...card} />)}
          </div>
        )}

        {/* Code blocks */}
        {section.codeBlocks?.map((cb, i) => <CodeBlock key={i} language={cb.language} title={cb.title}>{cb.code}</CodeBlock>)}

        {/* Steps */}
        {section.steps?.length > 0 && <StepsList steps={section.steps} />}

        {/* Section-level endpoints */}
        {section.endpoints?.map((ep, i) => <Endpoint key={i} {...ep} />)}

        {/* Subsections */}
        {section.subsections?.map((sub) => (
          <div key={sub.id}>
            <SubHeading id={sub.id}>{sub.title}</SubHeading>
            {sub.content && <MarkdownContent>{sub.content}</MarkdownContent>}
            {sub.callouts?.map((c, i) => <Callout key={i} {...c} />)}
            {sub.codeBlocks?.map((cb, i) => <CodeBlock key={i} language={cb.language} title={cb.title}>{cb.code}</CodeBlock>)}
            {sub.steps?.length > 0 && <StepsList steps={sub.steps} />}
            {sub.endpoints?.map((ep, i) => <Endpoint key={i} {...ep} />)}
          </div>
        ))}
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Main Page                                                         */
/* ------------------------------------------------------------------ */
export default function DocumentationPage({ isPublic = false }) {
  const location = useLocation();
  const { isAdmin } = useAuth() || {};
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState('introduction');
  const mainRef = useRef(null);

  // Fetch sections
  const fetchSections = useCallback(async () => {
    try {
      setError(null);
      const res = await docsApi.list();
      const data = res.data?.data || [];
      setSections(data);
    } catch (err) {
      console.error('Failed to fetch docs:', err);
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSections(); }, [fetchSections]);

  // Scroll to hash on mount
  useEffect(() => {
    if (location.hash && !loading) {
      setTimeout(() => {
        const el = document.getElementById(location.hash.slice(1));
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [location.hash, loading]);

  // Build TOC from data
  const toc = sections.map(s => ({
    id: s.layerId || s.sectionId,
    label: s.title,
    icon: ICON_MAP[s.icon] || Book,
  }));

  // Intersection observer for active section tracking
  useEffect(() => {
    if (toc.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        }
      },
      { rootMargin: '-80px 0px -60% 0px', threshold: 0 }
    );
    for (const { id } of toc) {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    }
    return () => observer.disconnect();
  }, [toc.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const scrollTo = useCallback((id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  }, []);

  // Public layout wrapper — natural page scroll
  const PublicWrapper = ({ children }) => (
    <div className="min-h-screen bg-background">
      {/* Sticky public header */}
      <header className="sticky top-0 z-50 flex items-center justify-between h-14 px-6 border-b bg-card/95 backdrop-blur">
        <a href="https://trucontext.ai" className="flex items-center gap-2 text-foreground hover:opacity-80">
          <div className="flex items-center justify-center w-7 h-7 rounded-md bg-primary text-primary-foreground text-xs font-bold">TC</div>
          <span className="font-semibold text-sm">TruContext</span>
        </a>
        <div className="flex items-center gap-3">
          <a href="/login" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Sign In</a>
          <a href="/signup" className="text-sm bg-primary text-primary-foreground px-3 py-1.5 rounded-md hover:bg-primary/90 transition-colors">Sign Up</a>
        </div>
      </header>
      {children}
    </div>
  );

  // Loading state
  if (loading) {
    const loader = (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-3 text-muted-foreground">Loading documentation...</span>
      </div>
    );
    return isPublic ? <PublicWrapper>{loader}</PublicWrapper> : loader;
  }

  // Error state
  if (error) {
    const errorView = (
      <div className="flex flex-col items-center justify-center py-20 space-y-4">
        <AlertCircle className="h-8 w-8 text-destructive" />
        <p className="text-muted-foreground">Failed to load documentation</p>
        <p className="text-xs text-muted-foreground/60">{error}</p>
        <button onClick={fetchSections} className="text-sm text-primary hover:underline">
          Try again
        </button>
      </div>
    );
    return isPublic ? <PublicWrapper>{errorView}</PublicWrapper> : errorView;
  }

  // --- Sidebar TOC (shared between both layouts) ---
  const sidebarNav = (
    <>
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-3 px-2">
        On this page
      </p>
      <nav className="space-y-0.5">
        {toc.map((item) => (
          <button
            key={item.id}
            onClick={() => scrollTo(item.id)}
            className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-xs transition-colors ${
              activeSection === item.id
                ? 'bg-accent text-foreground font-medium'
                : 'text-muted-foreground hover:text-foreground hover:bg-accent/50'
            }`}
          >
            <item.icon className="h-3.5 w-3.5 shrink-0" />
            {item.label}
          </button>
        ))}
      </nav>
      {isAdmin && (
        <div className="mt-6 px-2">
          <button
            onClick={async () => {
              if (window.confirm('Reset all documentation sections to defaults?')) {
                try {
                  await docsApi.reseed();
                  fetchSections();
                } catch (err) {
                  alert('Reseed failed: ' + (err.response?.data?.error || err.message));
                }
              }
            }}
            className="w-full flex items-center justify-center gap-1.5 text-[10px] text-muted-foreground hover:text-foreground border rounded py-1.5 transition-colors"
          >
            <RotateCcw className="h-3 w-3" />
            Reset to Defaults
          </button>
        </div>
      )}
    </>
  );

  // --- Main content (shared) ---
  const mainContent = (
    <>
      {sections.map((section) => (
        <DocSection
          key={section.layerId || section.sectionId}
          section={section}
          isAdmin={isAdmin}
          onSectionUpdated={fetchSections}
        />
      ))}
      <div className="mt-16 pt-8 border-t text-center text-xs text-muted-foreground">
        <p>TruContext API v1 &mdash; Values as Infrastructure for AI</p>
        <p className="mt-1">
          Questions? Reach us at{' '}
          <a href="mailto:support@trucontext.ai" className="text-primary hover:underline">
            support@trucontext.ai
          </a>
        </p>
      </div>
    </>
  );

  // --- PUBLIC LAYOUT: natural page scroll, sticky nav ---
  if (isPublic) {
    return (
      <PublicWrapper>
        <div className="flex max-w-7xl mx-auto w-full">
          {/* Sticky sidebar */}
          <aside className="w-56 shrink-0 hidden lg:block">
            <div className="sticky top-14 h-[calc(100vh-3.5rem)] overflow-y-auto py-6 px-3 border-r">
              {sidebarNav}
            </div>
          </aside>
          {/* Main content — natural scroll */}
          <main ref={mainRef} className="flex-1 min-w-0 px-8 lg:px-12 py-8">
            {mainContent}
          </main>
        </div>
      </PublicWrapper>
    );
  }

  // --- IN-APP LAYOUT: scroll container inside DashboardLayout ---
  return (
    <div className="flex -m-6" style={{ height: 'calc(100vh - 4rem)' }}>
      <aside className="w-56 shrink-0 border-r bg-card/30 overflow-y-auto py-4 px-3 hidden lg:block">
        {sidebarNav}
      </aside>
      <main ref={mainRef} className="flex-1 overflow-y-auto px-6 lg:px-10 py-8 max-w-4xl">
        {mainContent}
      </main>
    </div>
  );
}
